import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useMessage } from '@tmapy/intl';
import { useDispatch } from '@tmapy/redux';

import { DangerAlert, InfoAlert, WaitingAlert, BtnBar } from '@tmapy/style-guide';

import './DocList.scss';

import { DocListDefault } from './DocListDefault';
import { DocListDtmKad } from './DocListDtmKad';
import { DocListDtmkDti } from './DocListDtmkDti';
import { DocListDmvsGad } from './DocListDmvsGad';
import { DocListDmvsDti } from './DocListDmvsDti';
import { DocListDmvsCross } from './DocListDmvsCross';

import { DocFilter } from './DocFilter';
import { DocFilterDmvsDti } from './DocFilterDmvsDti';
import { DocFilterDmvsGad } from './DocFilterDmvsGad';
import { DocFilterDmvsCross } from './DocFilterDmvsCross';
import { DocFilterIsDtm } from './DocFilterIsDtm';
import { DocFilterDtmKad } from './DocFilterDtmKad';
import { DocExport, ExportMappingType } from './DocExport';
import { docListMessages } from './docsMessages';
import { DateRangeData, DateRangePickerBtn } from './DateRangePickerBtn';

import {
  DOC_STATE,
  DOC_STATES_FILTER_DMVS_CROSS,
  DOC_STATES_FILTER_DMVS_DTI,
  DOC_STATES_FILTER_DMVS_GAD,
  DOC_STATES_FILTER_IS_DTM,
  DOC_STATES_FILTER_KAD,
  DOC_STATES_MANAGER,
  DOC_STATES_MANAGER_EXCLUDED,
  DOC_STATES_PREJIMKA,
  DOC_ROUTER_FILTER_DMVS_CROSS,
  DOC_ROUTER_FILTER_DMVS_DTI,
  DOC_ROUTER_FILTER_DMVS_GAD,
  DOC_ROUTER_FILTER_IS_DTM,
  DOC_ROUTER_FILTER_PODANI_KAD,
} from '../constants';
import { MainContent } from '../../app/components/MainContent';
import { PageTitle } from '../../app/components/PageTitle';
import { TableWithAsideLayout } from '../../app/components/TableWithAsideLayout';
import { FilterBtn } from '../../app/components/FilterBtn';
import { APP_TYPE, QUERY_STATE } from '../../constants';
import { brnoDocsImportExternalDocsCreate } from '../../openapi/operations/brnoDocsImportExternalDocsCreate';
import { useBrnoDocsListResource } from '../../openapi/operations/brnoDocsList';
import { useDocsListResource } from '../../openapi/operations/docsList';
import { SSE_TYPE, useAppContext, useAuth, useSSE, useSelector } from '../../hooks';
import { setDocListFilter } from '../../redux/filter/actions';

export const getDocumentStates = (
  states: Partial<Record<string, readonly DOC_STATE[]>>,
  filterStates?: DOC_STATE[],
): string[] => {
  let documentStates: string[] = [];

  if (filterStates) {
    filterStates.forEach((stateIdent) => {
      const currentStates = states[stateIdent];
      if (currentStates) {
        documentStates = [...documentStates, ...currentStates];
      } else {
        documentStates = [...documentStates, stateIdent];
      }
    });
    return documentStates;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  for (const [_, val] of Object.entries(states)) {
    if (val) {
      documentStates = [...documentStates, ...val];
    }
  }
  return documentStates;
};

export type DocListProps = {
  docId?: number;
};

export const DocList: React.FC<DocListProps> = ({ docId }) => {
  const { incomingSSEEventType } = useSSE(SSE_TYPE.DOCS);
  const dispatch = useDispatch();
  const formatMessage = useMessage();
  const [isFilterOpen, setFilterOpen] = useState(false);
  const app = useAppContext();
  const { manager, customerType } = useSelector((state) => state.app.config);
  const filters = useSelector((state) => state.filter.docList);

  const menuRouteFilterId = useSelector((state) => state.menu.menuRouteFilterId);

  const Component = useMemo(() => {
    if (DOC_ROUTER_FILTER_DMVS_DTI.includes(menuRouteFilterId)) {
      return {
        list: DocListDmvsDti,
        filter: DocFilterDmvsDti,
        states: DOC_STATES_FILTER_DMVS_DTI,
        export: ExportMappingType.DMVS_DTI,
      };
    }
    if (DOC_ROUTER_FILTER_DMVS_GAD.includes(menuRouteFilterId)) {
      return {
        list: DocListDmvsGad,
        filter: DocFilterDmvsGad,
        states: DOC_STATES_FILTER_DMVS_GAD,
        export: ExportMappingType.DMVS_GAD,
      };
    }
    if (DOC_ROUTER_FILTER_IS_DTM.includes(menuRouteFilterId)) {
      return {
        list: DocListDtmkDti,
        filter: DocFilterIsDtm,
        states: DOC_STATES_FILTER_IS_DTM,
        export: ExportMappingType.DTMK_DTI,
      };
    }
    if (DOC_ROUTER_FILTER_DMVS_CROSS.includes(menuRouteFilterId)) {
      return {
        list: DocListDmvsCross,
        filter: DocFilterDmvsCross,
        states: DOC_STATES_FILTER_DMVS_CROSS,
        export: ExportMappingType.DMVS_CROSS,
      };
    }
    if (DOC_ROUTER_FILTER_PODANI_KAD.includes(menuRouteFilterId)) {
      return {
        list: DocListDtmKad,
        filter: DocFilterDtmKad,
        states: DOC_STATES_FILTER_KAD,
        export: ExportMappingType.DTMK_KAD,
      };
    }

    return {
      list: DocListDefault,
      filter: DocFilter,
      states: DOC_STATES_MANAGER,
      export: ExportMappingType.DEFAULT,
    };
  }, [menuRouteFilterId]);

  // filtrovani na zaklade externiho vstupu docId - nefiltruje se konkretni typ formulare
  useEffect(() => {
    if (!docId) return;
    dispatch(setDocListFilter({ id: docId, prejimka_type__in: [] }));
  }, [docId]);

  const query: Record<string, string | number | string[] | DOC_STATE[]> = {};
  for (const [key, val] of Object.entries(filters)) {
    if (typeof val !== 'undefined' && val !== '') {
      (query as any)[key] = val;
    }
  }

  if (!(query.state__in as DOC_STATE[])?.length) {
    if (app.appType === APP_TYPE.PREJIMKA) {
      query.state__in = getDocumentStates(DOC_STATES_PREJIMKA);
    } else {
      query.state__not_in = getDocumentStates(DOC_STATES_MANAGER_EXCLUDED);
    }
  } else {
    const docStates = app.appType === APP_TYPE.PREJIMKA ? DOC_STATES_PREJIMKA : Component.states;
    query.state__in = getDocumentStates(docStates, query.state__in as DOC_STATE[]);
  }

  // Brno GP - "customerType": "gp",
  const { queryState, data, fetchMore, refetch } = customerType === 'gp' 
    ? useBrnoDocsListResource(query, false) 
    : useDocsListResource(query, false);

  useEffect(() => {
    if (incomingSSEEventType === SSE_TYPE.DOCS) {
      refetch();
    }
  }, [incomingSSEEventType]);

  const docList = data?.results ?? [];
  const docCount = data?.count;

  // ACL
  const auth = useAuth();
  const acl = useMemo(
    () => ({
      docsActionImportExternalDocs: auth.hasPermission('BrnoDocs', 'import_external_docs', data),
    }),
    [auth, data],
  );

  const handleFilterBtnClick = useCallback(() => {
    setFilterOpen((prev) => !prev);
  }, []);

  const handleNextDocsBtnClick = useCallback(() => {
    if (data?.next) {
      fetchMore(data.next);
    }
  }, [data?.next, fetchMore]);

  const handleDataLoad = useCallback((rangeData: DateRangeData) => {
    brnoDocsImportExternalDocsCreate({
      request: {
        closed__gte: rangeData.start.toISOString(),
        closed__lte: rangeData.end.toISOString(),
      },
    });
  }, []);

  const isFiltered = Object.values(filters).some((filter) => filter);

  return (
    <TableWithAsideLayout
      asideLabel={formatMessage(docListMessages.docListFiltrLabel)}
      isAsideOpen={isFilterOpen}
      onAsideClose={handleFilterBtnClick}
    >
      <MainContent width='fit' scrollRestorationPrefix='doclist'>
        <article className='sg-u-vs-2'>
          <header className='sg-a-d-f sg-a-jc-sb sg-a-ai-b sg-a-cg-2'>
            <PageTitle>
              <FormattedMessage {...docListMessages.docListPageTitle} />
            </PageTitle>
            <BtnBar>
              <FilterBtn isFiltered={isFiltered} onClick={handleFilterBtnClick} />
              {manager?.modules?.docExport && (
                <DocExport
                  query={query}
                  exportType={Component.export}
                  label={formatMessage(docListMessages.docListExportCsvLabel)}
                  fileName={'seznam_dokumentaci.csv'}
                />
              )}
              {acl.docsActionImportExternalDocs && app.appType === APP_TYPE.PREJIMKA && (
                <DateRangePickerBtn
                  label={formatMessage(docListMessages.docListImportExternalDocs)}
                  onConfirm={handleDataLoad}
                />
              )}
            </BtnBar>
          </header>
          {queryState === QUERY_STATE.WAITING && docList.length < 1 && (
            <WaitingAlert>
              <FormattedMessage {...docListMessages.docListStateWaiting} />
            </WaitingAlert>
          )}
          {queryState === QUERY_STATE.FAIL && docList.length < 1 && (
            <DangerAlert>
              <FormattedMessage {...docListMessages.docListStateFail} />
            </DangerAlert>
          )}
          {queryState === QUERY_STATE.SUCCESS && docList.length < 1 && (
            <>
              {isFiltered ? (
                <InfoAlert>
                  <FormattedMessage {...docListMessages.docListStateFilteredEmpty} />
                </InfoAlert>
              ) : (
                <InfoAlert>
                  <FormattedMessage {...docListMessages.docListStateEmpty} />
                </InfoAlert>
              )}
            </>
          )}
          {docList.length > 0 && (
            <Component.list
              docList={docList}
              queryState={queryState}
              docCount={docCount}
              handleNextDocsBtnClick={handleNextDocsBtnClick}
            />
          )}
        </article>
      </MainContent>
      <Component.filter filters={filters} customerType={customerType} dispatch={dispatch} />
    </TableWithAsideLayout>
  );
};
